/**
 * Pure computation for bulk-adding collection cards to a deck.
 * Handles copy-limit enforcement, existing card detection, and token auto-routing.
 */
import { canHaveUnlimitedCopies, getDefaultZoneForCard, getMaxCopiesForFormat } from '@/lib/deckValidation';
import type { AddDeckCardInput } from '@/services/decks';
import type { EnrichedCollectionCard } from '@/types/card';
import type { DeckCard, DeckFormat, DeckZone } from '@/types/deck';

export interface BulkDeckAddResult {
  toAdd: AddDeckCardInput[];
  toUpdate: { cardId: string; newQuantity: number }[];
  skipped: number;
}

/**
 * Computes which cards to add/update and which to skip based on copy limits.
 *
 * For each selected card:
 * 1. Tokens/emblems are routed to the `tokens` zone regardless of targetZone
 * 2. If the exact scryfall_id + zone exists in the deck, increment quantity
 * 3. Otherwise, create a new deck card entry
 * 4. Cards exceeding the format's copy limit (by card name, across all zones) are skipped
 */
export function computeBulkDeckAdditions(
  selectedCards: EnrichedCollectionCard[],
  deckId: string,
  deckFormat: DeckFormat | null,
  allDeckCards: DeckCard[],
  targetZone: DeckZone,
): BulkDeckAddResult {
  const toAdd: AddDeckCardInput[] = [];
  const toUpdate: { cardId: string; newQuantity: number }[] = [];
  let skipped = 0;

  const maxCopies = getMaxCopiesForFormat(deckFormat);

  // Build a map of existing copy counts by card name (lowercase) for this deck
  const deckCards = allDeckCards.filter((dc) => dc.deck_id === deckId);
  const copyCountByName = new Map<string, number>();
  for (const dc of deckCards) {
    const name = (dc.card_name ?? '').toLowerCase();
    if (!name) continue;
    // Don't count maybeboard or tokens toward copy limits
    if (dc.zone === 'maybeboard' || dc.zone === 'tokens') continue;
    copyCountByName.set(name, (copyCountByName.get(name) ?? 0) + dc.quantity);
  }

  // Index existing deck cards by scryfall_id + zone for exact match detection
  const existingByKey = new Map<string, DeckCard>();
  for (const dc of deckCards) {
    existingByKey.set(`${dc.scryfall_id}:${dc.zone}`, dc);
  }

  // Track updates already queued in this batch (to avoid double-updating the same card)
  const updatedCardIds = new Set<string>();
  // Track how many copies we're adding per name in this batch
  const addedByName = new Map<string, number>();

  for (const selected of selectedCards) {
    const card = selected.card;
    const cardNameLower = card.name.toLowerCase();

    // Determine effective zone: tokens always go to tokens zone
    const effectiveZone = getDefaultZoneForCard(card) === 'tokens' ? 'tokens' : targetZone;

    // Check copy limit (tokens and maybeboard are exempt)
    if (effectiveZone !== 'tokens' && effectiveZone !== 'maybeboard') {
      const unlimited = canHaveUnlimitedCopies({
        name: card.name,
        oracle_text: card.oracle_text,
        type_line: card.type_line,
        layout: card.layout,
      });

      if (!unlimited) {
        const existingCount = copyCountByName.get(cardNameLower) ?? 0;
        const batchAdded = addedByName.get(cardNameLower) ?? 0;
        const totalCount = existingCount + batchAdded;

        if (totalCount >= maxCopies) {
          skipped++;
          continue;
        }
      }
    }

    // Check for exact match (same scryfall_id + zone) in deck
    const key = `${card.id}:${effectiveZone}`;
    const existing = existingByKey.get(key);

    if (existing && !updatedCardIds.has(existing.id)) {
      // Increment existing card's quantity by 1
      toUpdate.push({ cardId: existing.id, newQuantity: existing.quantity + 1 });
      updatedCardIds.add(existing.id);
    } else {
      // Add as new entry
      toAdd.push({
        scryfall_id: card.id,
        card_name: card.name,
        quantity: 1,
        zone: effectiveZone,
      });
    }

    // Track the added copy for subsequent limit checks in this batch
    if (effectiveZone !== 'tokens' && effectiveZone !== 'maybeboard') {
      addedByName.set(cardNameLower, (addedByName.get(cardNameLower) ?? 0) + 1);
    }
  }

  return { toAdd, toUpdate, skipped };
}
